import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, MapPin, Navigation } from "lucide-react";
import ShareStrip from "@/components/common/ShareStrip";

// SB Media Studio — Lakeland, Polk County, Florida
const MAPS_QUERY = "SB Media Studio Lakeland FL";
const MAPS_EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(
  MAPS_QUERY,
)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;
const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  MAPS_QUERY,
)}`;
const MAPS_OPEN_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  MAPS_QUERY,
)}`;

const LocationModal = ({ open, onClose }) => {
  // Body scroll lock
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] overflow-y-auto overscroll-contain flex items-start md:items-center justify-center p-4 md:p-8"
          data-testid="location-modal"
        >
          <button
            type="button"
            aria-label="Close location modal"
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            data-testid="location-modal-backdrop"
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-4xl my-4 md:my-0 rounded-3xl bg-[#0a0510] border border-white/10 overflow-hidden shadow-[0_40px_120px_-20px_rgba(255,0,85,0.3)]"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#ff0055]/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#00f0ff]/12 blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="relative flex items-center justify-between gap-4 px-5 md:px-7 py-4 border-b border-white/10 bg-[#11091a]/70 backdrop-blur">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#ff0055]/15 border border-[#ff0055]/30 text-[#ff0055] flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#ff0055]">
                    Studio location
                  </div>
                  <div className="font-display font-black text-lg md:text-xl tracking-tight truncate">
                    SB Media Studio — Lakeland, FL
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={MAPS_DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-get-directions"
                  className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00f0ff] hover:text-white transition-colors px-3 py-2 rounded-full border border-[#00f0ff]/30 hover:border-[#00f0ff] whitespace-nowrap"
                >
                  <Navigation size={12} /> Directions
                </a>
                <a
                  href={MAPS_OPEN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-open-google"
                  className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#a395c3] hover:text-white transition-colors px-3 py-2 rounded-full border border-white/10 hover:border-white/30 whitespace-nowrap"
                >
                  Open in Maps
                  <ExternalLink size={11} />
                </a>
                
                <ShareStrip campaign="studio_location" title="SB Media Studio — Lakeland, FL" text="Premium podcast + video studio in Lakeland. Come take a look." className="mr-1" />
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="location-modal-close"
                  aria-label="Close"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 flex items-center justify-center transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Map iframe */}
            <div className="relative bg-white h-[420px] md:h-[560px]">
              <iframe
                title="SB Media Studio location on Google Maps"
                src={MAPS_EMBED_URL}
                data-testid="location-map-iframe"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            {/* Footer helper (mobile links) */}
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 md:px-7 py-3 border-t border-white/10 bg-[#11091a]/70 backdrop-blur">
              <div className="text-[11px] text-[#a395c3]">
                Lakeland, Polk County · Florida
              </div>
              <div className="sm:hidden flex items-center gap-3">
                <a
                  href={MAPS_DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-footer-directions"
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-[#00f0ff]"
                >
                  <Navigation size={11} /> Directions
                </a>
                <a
                  href={MAPS_OPEN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-footer-open-google"
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-[#a395c3]"
                >
                  Open in Maps
                  <ExternalLink size={11} />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LocationModal;
